/**
 * Locks for the auto-seat dialog: "these people are my core — keep them exactly
 * there, and shuffle everyone else around them."
 *
 * A lock is a whole PARTY (a guest and their plus-one chairs) at the exact chairs
 * it has now — in the preview, or in the room for someone already seated. The
 * dialog holds them in its own state and hands them to `planAutoSeat` as
 * `locks`; nothing here is ever sent to the server. Apply writes the plan, and a
 * locked chair is just an ordinary assignment inside it.
 */
import { lockKeyOf, type AutoSeatPlan, type LockedChair, type PlannedChair } from './autoSeat';
import { seatMapFor } from './seatingCandidates';
import type { SeatAssignment, SeatingTable } from '../types/seating';

const toLock = (chair: PlannedChair): LockedChair => ({
  tableId: chair.tableId,
  seatIndex: chair.seatIndex,
  seatKind: chair.seatKind,
  inviteeId: chair.inviteeId,
  accompaniesInviteeId: chair.accompaniesInviteeId,
  guestLabel: chair.guestLabel,
  name: chair.name,
  companion: chair.companion,
});

/** Whose party a chair belongs to; null for a chair with no owner. */
export const partyKeyOf = (chair: PlannedChair): string | null => lockKeyOf(toLock(chair));

/** The lock for the party that owns `key`, from the chairs the plan proposes. */
export function lockFromPlan(plan: AutoSeatPlan, key: string): LockedChair[] {
  return plan.placements.filter((chair) => partyKeyOf(chair) === key).map(toLock);
}

/** Every party currently sitting in the room, as the lock that would keep them where they are. */
export function seatedParties(tables: SeatingTable[]): Array<{ key: string; name: string; chairs: LockedChair[] }> {
  const byKey = new Map<string, { key: string; name: string; chairs: LockedChair[] }>();
  for (const table of tables) {
    for (const [seatIndex, assignment] of [...seatMapFor(table).entries()].sort((a, b) => a[0] - b[0])) {
      const chair = lockedChairOf(table.id, seatIndex, assignment);
      const key = chair ? lockKeyOf(chair) : null;
      if (!chair || !key) continue;
      const entry = byKey.get(key) ?? { key, name: '', chairs: [] };
      entry.chairs.push(chair);
      if (!chair.companion) entry.name = chair.name;
      byKey.set(key, entry);
    }
  }
  // A party we only met through a plus-one chair has no name of its own to offer.
  return [...byKey.values()].filter((entry) => entry.name !== '');
}

function lockedChairOf(tableId: string, seatIndex: number, assignment: SeatAssignment): LockedChair | null {
  if (assignment.seatKind === 'invitee' && assignment.inviteeId) {
    return {
      tableId,
      seatIndex,
      seatKind: 'invitee',
      inviteeId: assignment.inviteeId,
      accompaniesInviteeId: null,
      guestLabel: '',
      name: assignment.displayName || 'Guest',
      companion: false,
    };
  }
  if (assignment.seatKind === 'guest') {
    const companion = Boolean(assignment.accompaniesInviteeId);
    const label = assignment.guestLabel || assignment.displayName || 'Guest';
    return {
      tableId,
      seatIndex,
      seatKind: 'guest',
      inviteeId: null,
      accompaniesInviteeId: assignment.accompaniesInviteeId ?? null,
      guestLabel: label,
      name: label,
      companion,
    };
  }
  return null;
}

/** "Locked 6 · shuffling 38": people the plan pinned, and people it is free to move. */
export function lockCounts(plan: AutoSeatPlan): { locked: number; shuffling: number } {
  const people = plan.placements.filter((chair) => !chair.companion);
  const lockedShown = people.filter((chair) => chair.locked).length;
  return { locked: plan.lockedPeople, shuffling: people.length - lockedShown };
}
